import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Video, 
  Sprout, 
  TrendingUp, 
  Rocket, 
  Grid3X3, 
  List, 
  Play, 
  ArrowRight,
  Eye,
  Clock,
  Lock,
  Crown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api, { getFullImageUrl } from '../services/api';
import './Videos.css';

const Videos = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [showVipModal, setShowVipModal] = useState(false);
  const { isAuthenticated, isVip } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await api.get('/videos', { params: { limit: 20 } });
      if (response.data.success) {
        setVideos(response.data.data.videos);
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { id: 'all', label: 'All Videos', icon: Video },
    { id: 'beginner', label: 'Beginner', icon: Sprout },
    { id: 'intermediate', label: 'Intermediate', icon: TrendingUp },
    { id: 'advanced', label: 'Advanced', icon: Rocket },
  ];

  const filteredVideos = videos.filter(video => {
    if (activeFilter === 'all') return true;
    return video.level?.toLowerCase() === activeFilter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle video URL - if relative path, convert to full URL
  const getVideoUrl = (url) => {
    return getFullImageUrl(url, '');
  };

  const handlePlayVideo = (video) => {
    // Check VIP permission
    if (video.isVip && !isVip) {
      // Non-VIP users cannot play VIP videos
      setShowVipModal(true);
      return;
    }
    // If clicking the currently playing video, stop playback; otherwise play new video
    if (playingVideoId === video.id) {
      setPlayingVideoId(null);
    } else {
      setPlayingVideoId(video.id);
    }
  };

  // Handle VIP upgrade
  const handleUpgradeVip = () => {
    setShowVipModal(false);
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/videos' } });
    } else {
      navigate('/vip');
    }
  };

  if (loading) {
    return (
      <div className="videos-page videos-page--loading premium-page">
        <div className="videos-page__skeleton"></div>
      </div>
    );
  }

  return (
    <div className="videos-page premium-page">
      {/* Background */}
      <div className="videos-page__bg" />

      <div className="videos-page__container">
        {/* Header */}
        <header className="videos-page__header">
          <div className="videos-page__title-section">
            <h1 className="videos-page__title">
              <GraduationCap size={32} className="videos-page__title-icon" />
              Trading Academy
            </h1>
            <p className="videos-page__subtitle">
              Master the art of trading with our comprehensive video library
            </p>
          </div>

          <div className="videos-page__controls">
            {/* Filters */}
            <div className="videos-page__filters">
              {filters.map(filter => {
                const IconComponent = filter.icon;
                return (
                  <button
                    key={filter.id}
                    className={`videos-page__filter ${activeFilter === filter.id ? 'videos-page__filter--active' : ''}`}
                    onClick={() => setActiveFilter(filter.id)}
                  >
                    <IconComponent size={16} />
                    <span>{filter.label}</span>
                  </button>
                );
              })}
            </div>

            {/* View Toggle */}
            <div className="videos-page__view-toggle">
              <button
                className={`videos-page__view-btn ${viewMode === 'grid' ? 'videos-page__view-btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 size={20} />
              </button>
              <button
                className={`videos-page__view-btn ${viewMode === 'list' ? 'videos-page__view-btn--active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Video Grid */}
        <div className={`videos-page__grid videos-page__grid--${viewMode}`}>
          {filteredVideos.length === 0 ? (
            <div className="videos-page__empty">
              <Video size={48} />
              <p>No videos found</p>
            </div>
          ) : (
            filteredVideos.map((video, index) => (
              <article 
                key={video.id} 
                className={`video-card ${video.isVip ? 'video-card--vip' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className={`video-card__thumbnail-wrapper ${playingVideoId === video.id ? 'video-card__thumbnail-wrapper--playing' : ''}`}>
                  {playingVideoId === video.id ? (
                    <video
                      controls
                      autoPlay
                      className="video-card__video"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 10
                      }}
                      src={getVideoUrl(video.videoUrl)}
                      onError={(e) => {
                        console.error('Video playback error:', e);
                        setPlayingVideoId(null);
                        alert('Video loading failed, please check if the video URL is correct');
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onEnded={() => setPlayingVideoId(null)}
                    >
                      Your browser does not support video playback.
                    </video>
                  ) : (
                    <>
                      {/* Use video as thumbnail preview */}
                      <video
                        className="video-card__thumbnail video-card__thumbnail--video"
                        src={getVideoUrl(video.videoUrl)}
                        muted
                        preload="metadata"
                        onLoadedData={(e) => {
                          // Jump to 1 second after video loads as preview frame
                          e.target.currentTime = 1;
                        }}
                        onError={(e) => {
                          // Hide when video loading fails
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="video-card__overlay">
                        <button 
                          className="video-card__play-btn"
                          onClick={() => handlePlayVideo(video)}
                        >
                          {video.isVip && !isVip ? (
                            <Lock size={32} />
                          ) : (
                            <Play size={32} fill="currentColor" />
                          )}
                        </button>
                      </div>
                      <span className="video-card__duration">
                        <Clock size={12} /> {video.duration}
                      </span>
                      {video.isVip && (
                        <span className="video-card__vip-badge">
                          <Crown size={12} /> VIP Exclusive
                        </span>
                      )}
                      {/* VIP Lock Overlay */}
                      {video.isVip && !isVip && (
                        <div className="video-card__vip-lock">
                          <Lock size={24} />
                          <span>VIP Member Exclusive</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="video-card__content">
                  <div className="video-card__meta">
                    <span className={`video-card__level video-card__level--${video.level?.toLowerCase()}`}>
                      {video.level}
                    </span>
                    <span className="video-card__views">
                      <Eye size={14} /> {video.views?.toLocaleString() || 0}
                    </span>
                  </div>
                  <h3 className="video-card__title">{video.title}</h3>
                  {video.description && (
                    <p className="video-card__description">{video.description}</p>
                  )}
                  
                  {video.quoteText && (
                    <blockquote className="video-card__quote">
                      <p>"{video.quoteText}"</p>
                      <cite>— {video.quoteAuthor}</cite>
                    </blockquote>
                  )}

                  <div className="video-card__footer">
                    <span className="video-card__date">{formatDate(video.createdAt)}</span>
                    <button 
                      className="video-card__watch-btn"
                      onClick={() => handlePlayVideo(video)}
                    >
                      {playingVideoId === video.id ? 'Stop' : 'Watch Now'}
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>

      {/* VIP Upgrade Modal */}
      {showVipModal && (
        <div className="vip-modal-overlay" onClick={() => setShowVipModal(false)}>
          <div className="vip-modal vip-modal--video" onClick={(e) => e.stopPropagation()}>
            <button className="vip-modal__close" onClick={() => setShowVipModal(false)}>×</button>
            <div className="vip-modal__icon">
              <Crown size={48} />
            </div>
            <h2 className="vip-modal__title">VIP Member Exclusive Content</h2>
            <p className="vip-modal__desc">
              This video is exclusive to VIP members. Upgrade to VIP to watch all videos.
            </p>
            <div className="vip-modal__actions">
              <button className="vip-modal__btn vip-modal__btn--cancel" onClick={() => setShowVipModal(false)}>
                Maybe Later
              </button>
              <button className="vip-modal__btn vip-modal__btn--confirm" onClick={handleUpgradeVip}>
                <Crown size={16} />
                Upgrade to VIP Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Videos;
